import { useEffect, useMemo, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import {
  roleCanViewAudit,
  type AuditListResponse,
  type PublicAuditEntry,
} from '@inventohub/shared';
import { ApiClientError, apiRequest } from '../lib/api';
import { useAuth } from '../lib/auth';

interface Filters {
  entityType: string;
  entityId: string;
  actor: string;
  from: string;
  to: string;
}

const EMPTY: Filters = { entityType: '', entityId: '', actor: '', from: '', to: '' };

export function AuditPage(): JSX.Element {
  const { user } = useAuth();
  const canRead = user ? roleCanViewAudit(user.role) : false;
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [applied, setApplied] = useState<Filters>(EMPTY);
  const [entries, setEntries] = useState<PublicAuditEntry[]>([]);
  const [entityTypes, setEntityTypes] = useState<string[]>([]);
  const [actors, setActors] = useState<AuditListResponse['actors']>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(canRead);

  const query = useMemo(() => {
    const params = new URLSearchParams();
    if (applied.entityType) params.set('entityType', applied.entityType);
    if (applied.entityId.trim()) params.set('entityId', applied.entityId.trim());
    if (applied.actor) params.set('actor', applied.actor);
    if (applied.from) params.set('from', applied.from);
    if (applied.to) params.set('to', applied.to);
    const encoded = params.toString();
    return encoded ? `/api/audit?${encoded}` : '/api/audit';
  }, [applied]);

  useEffect(() => {
    if (!canRead) return;
    let cancelled = false;
    void (async () => {
      try {
        const body = await apiRequest<AuditListResponse>(query);
        if (cancelled) return;
        setEntries(body.entries);
        setEntityTypes(body.entityTypes);
        setActors(body.actors);
        setError(null);
      } catch (caught: unknown) {
        if (cancelled) return;
        setError(
          caught instanceof ApiClientError ? caught.message : 'Could not load the audit log.',
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canRead, query]);

  if (!canRead) {
    return (
      <section className="card" data-testid="audit-page">
        <h2>Audit log</h2>
        <p className="status status--error" data-testid="audit-denied">
          Only an admin or equipment admin can read the audit log.
        </p>
      </section>
    );
  }

  function onFilter(event: FormEvent): void {
    event.preventDefault();
    setApplied({ ...filters, entityId: filters.entityId.trim() });
  }

  return (
    <section data-testid="audit-page">
      <div className="card">
        <h2>Audit log</h2>
        <p className="meta">Append-only. Times shown in America/Chicago.</p>
        {error ? (
          <p className="status status--error" data-testid="audit-error">
            {error}
          </p>
        ) : null}
      </div>

      <form className="card" onSubmit={onFilter} data-testid="audit-filters">
        <h2>Filter</h2>
        <label className="field">
          Entity type
          <select
            value={filters.entityType}
            onChange={(event) =>
              setFilters((current) => ({ ...current, entityType: event.target.value }))
            }
            data-testid="audit-entity-type"
          >
            <option value="">Any</option>
            {entityTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          Entity id
          <input
            type="text"
            autoComplete="off"
            value={filters.entityId}
            onChange={(event) =>
              setFilters((current) => ({ ...current, entityId: event.target.value }))
            }
            data-testid="audit-entity-id"
          />
        </label>
        <label className="field">
          Actor
          <select
            value={filters.actor}
            onChange={(event) =>
              setFilters((current) => ({ ...current, actor: event.target.value }))
            }
            data-testid="audit-actor"
          >
            <option value="">Any</option>
            {actors.map((actor) => (
              <option key={`${actor.kind}-${actor.id}`} value={actor.id}>
                {actor.name}
                {actor.kind === 'code' ? ' (event code)' : ''}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          From
          <input
            type="date"
            value={filters.from}
            onChange={(event) =>
              setFilters((current) => ({ ...current, from: event.target.value }))
            }
            data-testid="audit-from"
          />
        </label>
        <label className="field">
          To
          <input
            type="date"
            value={filters.to}
            onChange={(event) => setFilters((current) => ({ ...current, to: event.target.value }))}
            data-testid="audit-to"
          />
        </label>
        <button type="submit" className="button" data-testid="audit-apply">
          Apply filters
        </button>
      </form>

      {loading ? (
        <p className="meta" data-testid="audit-loading">
          Loading audit rows…
        </p>
      ) : null}

      {!loading && entries.length === 0 ? (
        <p className="meta" data-testid="audit-empty">
          No matching audit rows.
        </p>
      ) : null}

      {entries.map((entry) => (
        <article className="card" key={entry.id} data-testid={`audit-row-${entry.id}`}>
          <h2>{entry.action}</h2>
          <p className="meta" data-testid={`audit-meta-${entry.id}`}>
            {formatChicago(entry.at)} · {entry.actorName} · {entry.entityType} {entry.entityId}
          </p>
          {entry.before !== null || entry.after !== null ? (
            <details>
              <summary>Before / after</summary>
              <pre className="audit-json" data-testid={`audit-json-${entry.id}`}>
                {JSON.stringify({ before: entry.before, after: entry.after }, null, 2)}
              </pre>
            </details>
          ) : null}
        </article>
      ))}
    </section>
  );
}

function formatChicago(at: string): string {
  const parsed = Date.parse(at);
  if (Number.isNaN(parsed)) return at;
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(new Date(parsed));
}
